import { usersById, timesheetsForWeek, submittedTimesheets, listLeaveRequests, allEnquiries, allInvoices, allProjects, listNotes, listPurchaseOrders, listExpenses, listMailApprovals } from './db.js';
import { escapeHtml, money, titleCase } from './ui.js';
import { orgSetting } from './org.js';
import { fmtMoney, claimApproxText } from './expense-currency.js';
import { myFxRate } from './my-currency.js';
import { isUnknownV1 } from './invoice-class.js';
import { expectedMinutes, userFor } from './contract-hours.js';
import { isTimesheetExempt } from './timesheet-required.js';

// Notifications (design_handoff_track_process piece B, rebuilt to
// design_handoff_track_polish §6). THE RULE, unchanged and now stated on the
// sheet itself: a notification is a RECORD WAITING ON A DECISION OF YOURS.
// Progress, confirmations and other people's work never notify. There is no
// bell, no feed, and no unread count of things that merely happened — which is
// why there is no settings screen either.
//
// §6 changes three things about how that is presented:
//   · the trigger is a "need you" pill in the header line, not a bell in the
//     rail foot — a bell promises a feed, and this is not one;
//   · the panel is a 420px sheet down the right edge, over a scrim;
//   · rows group BY VERB — Approve, Reply, Decide — rather than by where they
//     came from, because the verb is what you are deciding between when you
//     look at the list.
//
// Everything is DERIVED from existing state (the eight events all exist as
// states in the app already) — no event pipeline, no new collection. Clearing
// is by RESOLUTION: approve the sheet and its row vanishes for everyone,
// because the state it derives from is gone. Only the informational "Earlier"
// group carries a read-marker (localStorage), and it is never in the badge.
//
// Deferred from the handoff, deliberately: calendar entries (needs a
// domain-wide Calendar scope grant — Karan admin action) and drawing-review
// comment notifications (comments don't persist yet — blocked on §11.5).

const MONEY = ['Owner', 'Director', 'Finance'];
// The three verbs, in the order the sheet draws them. Everything a row can be
// is one of these; a derivation that fits none of them is not a notification.
const VERBS = [
  ['approve', 'Approve'],
  ['reply', 'Reply'],
  ['decide', 'Decide'],
];
const HOT_DAYS = 3;   // older than this and the row's dot and age turn orange
const REVIEWERS = ['Owner', 'Director', 'Finance', 'Coordinator'];

const parse = (v) => {
  if (!v) return 0;
  const s = String(v).trim();
  if (s.includes(':')) { const [h, m] = s.split(':'); return (parseInt(h || 0, 10) * 60) + parseInt(m || 0, 10); }
  const f = parseFloat(s); return isNaN(f) ? 0 : Math.round(f * 60);
};
const hm = (min) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
const sheetMinutes = (s) => (s.rows || []).reduce((t, r) => t + Object.values(r.days || {}).reduce((x, v) => x + parse(v), 0), 0);
const mondayOf = (d) => { const m = new Date(d.getFullYear(), d.getMonth(), d.getDate()); m.setDate(m.getDate() - ((m.getDay() + 6) % 7)); return m; };
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const workingDaysSince = (from, now) => {
  let n = 0; const d = new Date(from);
  while (d < now) { const wd = d.getDay(); if (wd !== 0 && wd !== 6) n++; d.setDate(d.getDate() + 1); }
  return n;
};
const stamp = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

export function mountNotifications({ session, open }) {
  // THE PILL LIVES IN THE HEADER (§4's slot on a desk, the 48px app bar on a
  // phone — §13). Two mount points, ONE pill: it is moved between them on
  // resize rather than drawn twice, because two pills with two counts is the
  // bug this whole section exists to avoid.
  const deskSlot = document.getElementById('hdr-pill-slot');
  const phoneSlot = document.querySelector('.mobile-header');
  const slot = deskSlot || document.querySelector('.rail-foot');
  if (!slot || document.querySelector('.ntf-pill')) return;
  const email = String(session.email || '').toLowerCase();
  const isReviewer = (session.roles || []).some((r) => REVIEWERS.includes(r));
  const isMoney = (session.roles || []).some((r) => MONEY.includes(r));
  // A read-marker survived from the old "Earlier" group, which §6 retires: a
  // row here clears when the RECORD is resolved, never because it was looked
  // at. The marker is kept only so a row you have opened does not re-announce
  // itself in the same session before the underlying state has caught up.
  const SEEN_KEY = `ntf-seen-${email}`;
  const seen = () => { try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch (_) { return new Set(); } };
  const markSeen = (ids) => { try { localStorage.setItem(SEEN_KEY, JSON.stringify([...new Set([...seen(), ...ids])].slice(-200))); } catch (_) { /* ignore */ } };

  // The pill: 32px, a hairline, an orange dot with a halo, the count in mono,
  // then the words. It is ABSENT when nothing is waiting — an empty state in
  // the header would be a permanent reminder that there is nothing to remind
  // you of.
  const row = document.createElement('button');
  row.type = 'button';
  row.className = 'ntf-pill';
  row.hidden = true;
  row.innerHTML = `
    <span class="ntf-pill-dot" aria-hidden="true"></span>
    <span class="ntf-badge"></span>
    <span class="ntf-pill-text">need you</span>`;
  const place = () => {
    const want = (window.innerWidth <= 760 && phoneSlot) ? phoneSlot : slot;
    if (row.parentElement !== want) want.appendChild(row);
  };
  place();
  window.addEventListener('resize', place);
  const badge = row.querySelector('.ntf-badge');

  let panel = null;
  let events = { needs: [] };

  async function compute() {
    const now = new Date();
    const monday = mondayOf(now);
    const needs = [];
    const jobs = [];

    // 1 · Timesheets waiting on you (Coordinator+)
    if (isReviewer) jobs.push(submittedTimesheets().then((subs) => {
      subs.filter((s) => String(s.userEmail || '').toLowerCase() !== email).forEach((s) => needs.push({
        id: `ts-sub-${s.id}`, verb: 'approve',
        text: `${escapeHtml(titleCase(String(s.userEmail || '').split('@')[0].replace(/[._]/g, ' ')))} submitted <span style="font-family:var(--font-technical)">${hm(sheetMinutes(s))}</span> for week ${escapeHtml(String(s.weekStart || ''))}.`,
        action: 'Review the week', route: { type: 'screen', id: 'approvals' }, at: s.submittedAt,
      }));
    }).catch(() => {}));

    // 2 · Your sheet was sent back  +  3 · Lock deadline approaching
    jobs.push(Promise.all([timesheetsForWeek(isoDay(monday)), usersById().catch(() => ({}))]).then(([sheets, users]) => {
      // The expected week: the person's, else the company's (ASP 37:30), else none.
      const meDoc = userFor(users, session);
      const contractMin = expectedMinutes(meDoc, orgSetting('weeklyContractMinutes', null));
      const mine = sheets.find((s) => String(s.userEmail || '').toLowerCase() === email);
      if (mine && mine.status === 'rejected') {
        needs.push({
          id: `ts-back-${mine.id}`, verb: 'decide',
          text: `Your week was sent back${mine.reviewedBy ? ` by ${escapeHtml(mine.reviewedBy)}` : ''}${mine.reviewNote ? ` — “${escapeHtml(mine.reviewNote)}”` : ''}.`,
          action: 'Fix and resubmit', route: { type: 'screen', id: 'time' }, at: mine.reviewedAt,
        });
      }
      const lockSoon = now.getDay() === 1 ? now.getHours() >= 16 : now.getDay() === 2 && now.getHours() < 10;
      // Timesheets not required (Team, 26/10/2026): never nudged about the lock.
      if (lockSoon && !isTimesheetExempt(meDoc) && (!mine || mine.status === 'draft')) {
        const logged = mine ? sheetMinutes(mine) : 0;
        needs.push({
          id: `ts-lock-${isoDay(monday)}`, verb: 'decide',
          text: `Your timesheet locks Tuesday at 10:00 — <span style="font-family:var(--font-technical)">${hm(logged)}</span>${contractMin ? ` of <span style="font-family:var(--font-technical)">${hm(contractMin)}</span>` : ''} logged.`,
          action: 'Finish the week', route: { type: 'screen', id: 'time' }, at: now.toISOString(),
        });
      }
    }).catch(() => {}));

    // 4 · Leave needs approving (Coordinator+)
    if (isReviewer) jobs.push(listLeaveRequests().then((reqs) => {
      reqs.filter((r) => (r.status || 'pending') === 'pending').forEach((r) => needs.push({
        id: `leave-${r.id}`, verb: 'approve',
        text: `${escapeHtml(titleCase(String(r.userEmail || '').split('@')[0].replace(/[._]/g, ' ')))} requested leave ${escapeHtml(String(r.from || ''))} – ${escapeHtml(String(r.to || r.from || ''))}.`,
        action: 'Approve or decline', route: { type: 'screen', id: 'calendar' }, at: r.requestedAt || r.createdAt,
      }));
    }).catch(() => {}));

    // 5 · Enquiry unanswered after 3 working days (reviewers see the queue)
    if (isReviewer) jobs.push(allEnquiries().then((enqs) => {
      enqs.filter((e) => e.status === 'Enquiry' && e.receivedAt && workingDaysSince(new Date(e.receivedAt), now) >= 3)
        .slice(0, 5).forEach((e) => needs.push({
          id: `enq-${e.id}`, verb: 'reply',
          text: `${escapeHtml(e.clientName || e.company || e.number || 'An enquiry')} has had no reply for ${workingDaysSince(new Date(e.receivedAt), now)} working days.`,
          action: 'Open the enquiry', route: { type: 'enquiry', id: e.id }, at: e.receivedAt,
        }));
    }).catch(() => {}));

    // 6 · Invoices overdue (Finance+) — one row, not one per invoice
    if (isMoney) jobs.push(allInvoices().then((invs) => {
      const v1Money = orgSetting('money', {}) || {};   // Unknown (V1) invoices are never chased (invoice-class.js)
      const od = invs.filter((i) => i.paymentStatus === 'Invoiced' && !i.v2Voided && !isUnknownV1(i, v1Money) && i.dueDate && new Date(i.dueDate) < now);
      if (od.length) {
        const total = od.reduce((s, i) => s + (Number(i.amount) || 0), 0);
        needs.push({
          id: `inv-od-${isoDay(now)}`, verb: 'decide',
          text: `${od.length} invoice${od.length === 1 ? ' is' : 's are'} overdue — ${escapeHtml(money(total))} outstanding past due.`,
          action: 'Open invoices', route: { type: 'screen', id: 'invoices' }, at: now.toISOString(),
        });
      }
    }).catch(() => {}));

    // 7 · Issued, nothing invoiced (Finance+) — the work is delivered and the
    // fee is earned; finance is what's blocking the cash (Karan 21/08). One row
    // per project, newest first, capped.
    if (isMoney) jobs.push(Promise.all([allProjects(), allInvoices()]).then(([projs, invs]) => {
      const invoiced = new Set(invs.map((i) => String(i.projectRef || '').split('/').pop()).filter(Boolean));
      projs.filter((p) => ['Issued', 'Part Issued'].includes(p.status) && !invoiced.has(String(p.id)))
        .sort((a, b) => String(b.number || '').localeCompare(String(a.number || '')))
        .slice(0, 5).forEach((p) => needs.push({
          id: `issue-inv-${p.id}`, verb: 'decide',
          text: `<span style="font-family:var(--font-technical)">${escapeHtml(String(p.number || p.id))}</span> · ${escapeHtml(String(p.clientName || p.client || 'a project'))} is ${escapeHtml(String(p.status).toLowerCase())} with nothing invoiced.`,
          action: 'Raise the invoice', route: { type: 'project-no', id: p.number || p.id }, at: p.statusUpdatedAt || p.updatedAt,
        }));
    }).catch(() => {}));

    // 8 · Mentioned in a note. Under §6 this is a REPLY, not a footnote: being
    // named in a note is somebody asking you something, and the old "Earlier"
    // group was exactly the feed of things-that-happened the rule forbids.
    jobs.push(listNotes().then((notes) => {
      notes.filter((n) => Array.isArray(n.mentions) && n.mentions.map((m) => String(m).toLowerCase()).includes(email))
        .slice(0, 8).forEach((n) => needs.push({
          id: `note-${n.id}`, verb: 'reply',
          text: `${escapeHtml(n.userName || 'Someone')} mentioned you on <span style="font-family:var(--font-technical)">${escapeHtml(String(n.projectNo || ''))}</span>.`,
          action: 'Open the project', route: { type: 'project-no', id: n.projectNo }, at: n.createdAt,
        }));
    }).catch(() => {}));

    // 9 · Purchase orders and expenses waiting on a second pair of eyes
    // (Finance+). FOUR-EYES is the point: your own request is not yours to
    // approve, so anything you raised is filtered out here rather than shown
    // and refused.
    if (isMoney) jobs.push(Promise.all([listPurchaseOrders({}), listExpenses({}), myFxRate().catch(() => null)]).then(([pos, exps, fx]) => {
      pos.filter((po) => po.status === 'Requested' && String(po.userEmail || po.raisedBy || '').toLowerCase() !== email)
        .slice(0, 6).forEach((po) => needs.push({
          id: `po-${po.id}`, verb: 'approve',
          text: `${escapeHtml(titleCase(String(po.userEmail || po.raisedBy || '').split('@')[0].replace(/[._]/g, ' ')) || 'Someone')} raised a purchase order${po.amount ? ` for ${escapeHtml(money(Number(po.amount)))}` : ''}.`,
          sub: po.supplier || po.description || '',
          action: 'Approve or decline', route: { type: 'screen', id: 'procurement' }, at: po.requestedAt || po.createdAt,
        }));
      exps.filter((x) => x.status === 'requested' && String(x.userEmail || '').toLowerCase() !== email)
        .slice(0, 6).forEach((x) => needs.push({
          id: `exp-${x.id}`, verb: 'approve',
          text: `${escapeHtml(titleCase(String(x.userEmail || '').split('@')[0].replace(/[._]/g, ' ')) || 'Someone')} claimed an expense${x.amount ? ` of ${escapeHtml(x.currency === 'INR' ? [fmtMoney(Number(x.amount), 'INR', { whole: true }), claimApproxText(x, fx, (e) => Number(e.amount) || 0)].filter(Boolean).join(' · ') : money(Number(x.amount)))}` : ''}.`,
          sub: x.supplier || x.description || '',
          action: 'Approve or decline', route: { type: 'screen', id: 'procurement' }, at: x.claimedAt || x.createdAt,
        }));
    }).catch(() => {}));

    // 10 · Won, with nothing instructed. The enquiry said yes and the work has
    // no project to sit in, which is a decision nobody has taken yet.
    if (isReviewer) jobs.push(allEnquiries().then((enqs) => {
      enqs.filter((e) => e.status === 'Won' && !e.projectNo && !e.targetProjectRef)
        .slice(0, 5).forEach((e) => needs.push({
          id: `won-${e.id}`, verb: 'decide',
          text: `${escapeHtml(e.clientName || e.company || e.number || 'An enquiry')} is won with no instruction raised.`,
          sub: e.number ? String(e.number) : '',
          action: 'Instruct it', route: { type: 'enquiry', id: e.id }, at: e.wonAt || e.updatedAt,
        }));
    }).catch(() => {}));

    // Mail approval (07/10/2026): an email waiting on YOUR approval; your own
    // email back with changes, or approved and waiting for you to press Send.
    jobs.push(listMailApprovals(email).then((list) => {
      list.forEach((a) => {
        const subj = escapeHtml(String((a.draft && a.draft.subject) || '(no subject)').slice(0, 80));
        if (a.reviewerEmail === email && a.status === 'pending') {
          needs.push({ id: `apr-${a.id}`, verb: 'approve', text: `${escapeHtml(a.authorName || a.authorEmail)} asked you to approve “${subj}” before it goes.`, action: 'Read and approve', route: { type: 'mail-approval', id: a.id }, at: a.createdAt });
        } else if (a.authorEmail === email && a.status === 'changes') {
          needs.push({ id: `apr-${a.id}-ch`, verb: 'decide', text: `${escapeHtml(a.reviewerName || a.reviewerEmail)} asked for changes to “${subj}”.`, action: 'See the suggestions', route: { type: 'mail-approval', id: a.id }, at: a.reviewedAt || a.updatedAt });
        } else if (a.authorEmail === email && (a.status === 'approved' || a.status === 'unconfirmed') && (!a.autoSend || a.autoSendHeld)) {
          needs.push({ id: `apr-${a.id}-ok`, verb: 'decide', text: a.status === 'unconfirmed' ? `Track could not confirm “${subj}” went — check Sent.` : `${escapeHtml(a.reviewerName || a.reviewerEmail)} approved “${subj}” — press Send.`, action: 'Open it', route: { type: 'mail-approval', id: a.id }, at: a.reviewedAt || a.updatedAt });
        }
      });
    }).catch(() => {}));

    await Promise.all(jobs);
    // Oldest first inside a group: the thing that has been waiting longest is
    // the thing most likely to be someone else's blocker.
    needs.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
    events = { needs };
    row.hidden = !needs.length;
    badge.textContent = String(needs.length);
    // The rail carries the same truth, per screen (§6): a dot on Time,
    // Enquiries or Procurement means the screen behind it holds one of these.
    announce();
    if (panel) paintPanel();
  }

  // AGE IS THE ONLY URGENCY SIGNAL. Nothing is "high priority" because nobody
  // sets a priority on a purchase order; what makes a row hot is that it has
  // been sitting there. Three working-ish days and the dot and the age turn
  // orange, which is the same threshold the enquiry chase uses.
  const ageDays = (at) => {
    const d = at ? new Date(at) : null;
    if (!d || isNaN(d)) return 0;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  };
  const ageLabel = (at) => {
    const n = ageDays(at);
    if (!at) return '';
    return n === 0 ? 'today' : `${n}d`;
  };

  const rowHtml = (e) => {
    const hot = ageDays(e.at) >= HOT_DAYS;
    return `
    <div class="ntf-item" data-route='${escapeHtml(JSON.stringify(e.route))}' data-id="${escapeHtml(e.id)}">
      <span class="ntf-dot" style="background:${hot ? 'var(--brand-orange)' : 'var(--link)'};"></span>
      <div class="ntf-item-body">
        <p class="ntf-title">${e.text}</p>
        <span class="ntf-sub">${escapeHtml(e.sub || e.action || '')}</span>
      </div>
      <span class="ntf-age" style="${hot ? 'color:var(--brand-orange);' : ''}">${escapeHtml(ageLabel(e.at))}</span>
    </div>`;
  };


  // v4 (design p45): a drawer with Unread / All. Rows are still DERIVED from
  // live records and clear when the record is dealt with; "read" is the local
  // seen-marker, so a row you've looked at stops shouting without vanishing.
  let tab = 'unread';
  const relTime = (at) => {
    const d = at ? new Date(at) : null;
    if (!d || isNaN(d)) return '';
    const n = ageDays(at);
    if (n === 0) { const h = Math.floor((Date.now() - d.getTime()) / 3600000); return h <= 0 ? 'Just now' : `${h} hour${h === 1 ? '' : 's'} ago`; }
    if (n === 1) return 'Yesterday';
    if (n < 7) return `${n} days ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };
  const rowV4 = (e, read) => {
    const hot = ageDays(e.at) >= HOT_DAYS;
    return `
    <div class="ntf4-row${read ? '' : ' unread'}" data-route='${escapeHtml(JSON.stringify(e.route))}' data-id="${escapeHtml(e.id)}">
      ${read ? '' : `<span class="ntf4-dot${hot ? ' urgent' : ''}" aria-hidden="true"></span>`}
      <p class="ntf4-text">${e.text}${e.sub ? ` <span class="ntf4-sub">${escapeHtml(e.sub)}</span>` : ''}</p>
      <div class="ntf4-meta">${hot && !read ? 'Urgent · ' : ''}${escapeHtml(relTime(e.at))}</div>
      ${e.action ? `<button type="button" class="btn sm ntf4-act">${escapeHtml(e.action)}</button>` : ''}
    </div>`;
  };

  function paintPanel() {
    const s = seen();
    const unread = events.needs.filter((e) => !s.has(e.id));
    const list = tab === 'unread' ? unread : events.needs.slice().reverse();
    panel.innerHTML = `
      <div class="ntf4-head">
        <h2>Notifications</h2>
        <button type="button" class="linkbtn ntf4-markall"${unread.length ? '' : ' disabled'}>Mark all read</button>
        <button type="button" class="ntf4-close" aria-label="Close">${'&times;'}</button>
      </div>
      <div class="tabs ntf4-tabs" role="tablist">
        <button type="button" class="tab${tab === 'unread' ? ' active' : ''}" data-tab="unread" role="tab">Unread${unread.length ? ` <span class="n">${unread.length}</span>` : ''}</button>
        <button type="button" class="tab${tab === 'all' ? ' active' : ''}" data-tab="all" role="tab">All</button>
      </div>
      <div class="ntf4-scroll">
        ${list.length ? list.map((e) => rowV4(e, s.has(e.id))).join('') : `<p class="ntf4-empty">${tab === 'unread' ? 'Nothing new.' : 'Nothing is waiting on you.'}</p>`}
      </div>
      <div class="ntf4-foot">Each one is a record waiting on you, and clears when it’s dealt with. Track doesn’t email you about these.</div>`;
    panel.querySelectorAll('.ntf4-row').forEach((it) => it.addEventListener('click', () => {
      let route = null;
      try { route = JSON.parse(it.dataset.route); } catch (_) { /* ignore */ }
      markSeen([it.dataset.id]);
      closePanel();
      announce();
      if (route) open(route);
    }));
    panel.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => { tab = b.dataset.tab; paintPanel(); }));
    panel.querySelector('.ntf4-markall')?.addEventListener('click', () => { markSeen(events.needs.map((e) => e.id)); announce(); paintPanel(); });
    panel.querySelector('.ntf4-close')?.addEventListener('click', closePanel);
  }
  // The bell's blue dot (Home) follows the unread count.
  function announce() {
    const s = seen();
    const unread = events.needs.filter((e) => !s.has(e.id)).length;
    document.dispatchEvent(new CustomEvent('track:need-you', { detail: { count: events.needs.length, unread } }));
  }

  let scrim = null;
  function openPanel() {
    if (panel) { closePanel(); return; }
    scrim = document.createElement('div');
    scrim.className = 'ntf-scrim';
    scrim.addEventListener('click', closePanel);
    document.body.appendChild(scrim);

    panel = document.createElement('aside');
    panel.className = 'ntf-sheet ntf4';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Records waiting on you');
    document.body.appendChild(panel);
    paintPanel();
    row.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', onEsc);
  }
  const onEsc = (e) => { if (e.key === 'Escape') closePanel(); };
  function closePanel() {
    panel?.remove(); panel = null;
    scrim?.remove(); scrim = null;
    row.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onEsc);
  }
  // A route change means you went somewhere; the sheet has done its job.
  document.addEventListener('track:route', () => { if (panel) closePanel(); });

  row.addEventListener('click', openPanel);
  compute();
  setInterval(compute, 5 * 60 * 1000);          // data is TTL-cached — cheap
  document.addEventListener('track:fresh', compute);
  return { refresh: compute };
}
